<template>
    <div v-if="message" :class="notificationClasses" class="floating-notification">
        <div class="notification-content">
            <!-- Icon based on type -->
            <div class="notification-icon">
                <!-- Success Icon -->
                <svg v-if="type === 'success'" xmlns="http://www.w3.org/2000/svg" class="icon" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>

                <!-- Error Icon -->
                <svg v-if="type === 'error'" xmlns="http://www.w3.org/2000/svg" class="icon" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>

                <!-- Warning Icon -->
                <svg v-if="type === 'warning'" xmlns="http://www.w3.org/2000/svg" class="icon" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>

                <!-- Info Icon -->
                <svg v-if="type === 'info'" xmlns="http://www.w3.org/2000/svg" class="icon" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>

            <span class="notification-text">{{ message }}</span>

            <button v-if="actionLabel" @click="handleAction" class="action-button" type="button">
                {{ actionLabel }}
            </button>

            <button @click="closeNotification" class="close-button" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" class="close-icon" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    </div>
</template>

<script>
import { computed, watch, ref } from 'vue'

export default {
    name: 'FloatingNotification',
    props: {
        message: {
            type: String,
            default: null
        },
        type: {
            type: String,
            default: 'success',
            validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
        },
        duration: {
            type: Number,
            default: 3000 // 3 seconds
        },
        persistent: {
            type: Boolean,
            default: false // If true, won't auto-close
        },
        actionLabel: {
            type: String,
            default: null
        }
    },
    emits: ['close', 'action'],
    setup(props, { emit }) {
        const timeoutId = ref(null)

        // Computed class for notification styling based on type
        const notificationClasses = computed(() => {
            return {
                'notification-success': props.type === 'success',
                'notification-error': props.type === 'error',
                'notification-warning': props.type === 'warning',
                'notification-info': props.type === 'info'
            }
        })

        // Close notification
        const closeNotification = () => {
            if (timeoutId.value) {
                clearTimeout(timeoutId.value)
                timeoutId.value = null
            }
            emit('close')
        }

        const handleAction = () => {
            emit('action')
            closeNotification()
        }

        // Auto-close after duration if not persistent
        const setAutoClose = () => {
            if (!props.persistent && props.duration > 0) {
                if (timeoutId.value) {
                    clearTimeout(timeoutId.value)
                }
                timeoutId.value = setTimeout(() => {
                    closeNotification()
                }, props.duration)
            }
        }

        // Watch for message changes to reset auto-close timer
        watch(() => props.message, (newMessage) => {
            if (newMessage) {
                setAutoClose()
            }
        }, { immediate: true })

        return {
            notificationClasses,
            closeNotification,
            handleAction
        }
    }
}
</script>

<style scoped>
.floating-notification {
    position: fixed;
    top: 20px;
    right: 20px;
    background-color: #ffffff;
    border-radius: 8px;
    padding: 1rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    min-width: 320px;
    max-width: 400px;
    z-index: 1000;
    animation: slideInRight 0.3s ease-out;
}

@keyframes slideInRight {
    from {
        transform: translateX(100%);
        opacity: 0;
    }

    to {
        transform: translateX(0);
        opacity: 1;
    }
}

/* Success styling */
.notification-success {
    border-left: 4px solid #10b981;
}

.notification-success .notification-icon {
    color: #10b981;
}

.notification-success .notification-text {
    color: #065f46;
}

/* Error styling */
.notification-error {
    border-left: 4px solid #ef4444;
}

.notification-error .notification-icon {
    color: #ef4444;
}

.notification-error .notification-text {
    color: #7f1d1d;
}

/* Warning styling */
.notification-warning {
    border-left: 4px solid #f59e0b;
}

.notification-warning .notification-icon {
    color: #f59e0b;
}

.notification-warning .notification-text {
    color: #78350f;
}

/* Info styling */
.notification-info {
    border-left: 4px solid #3b82f6;
}

.notification-info .notification-icon {
    color: #3b82f6;
}

.notification-info .notification-text {
    color: #1e3a8a;
}

.notification-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.notification-icon {
    flex-shrink: 0;
}

.icon {
    width: 20px;
    height: 20px;
}

.notification-text {
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    flex: 1;
    line-height: 1.4;
}

.action-button {
    background-color: #4a69bd;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    padding: 0.4rem 0.75rem;
    font-family: 'Inter', sans-serif;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: background-color 0.15s ease-in-out;
}

.action-button:hover {
    background-color: #3d5aa0;
}

.close-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    transition: background-color 0.15s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.close-button:hover {
    background-color: #f3f4f6;
}

.close-icon {
    width: 16px;
    height: 16px;
    color: #6b7280;
}

/* Responsive design */
@media (max-width: 480px) {
    .floating-notification {
        top: 10px;
        right: 10px;
        left: 10px;
        min-width: auto;
        max-width: none;
    }
}
</style>